import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/** Fusionne des classes Tailwind en resolvant les conflits (la derniere gagne). */
export function cn(...entrees: ClassValue[]): string {
  return twMerge(clsx(entrees));
}

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * FORMATAGE
 *
 * Ces fonctions ne font que PRESENTER. Elles n'arrondissent ni ne recalculent
 * rien : les montants arrivent deja arretes par l'API, qui seule fait foi (§0.1).
 * Un arrondi ajoute ici ferait diverger l'ecran du PDF legal.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const formateurMonnaie = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formateurNombre = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const formateurDate = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const formateurDateLongue = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const formateurDateHeure = new Intl.DateTimeFormat('fr-FR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Montant en euros, format francais. `null` devient un tiret cadratin. */
export function montant(valeur: number | null | undefined): string {
  return valeur === null || valeur === undefined ? '—' : formateurMonnaie.format(valeur);
}

/** Quantite ou nombre libre, jusqu'a 4 decimales. */
export function nombre(valeur: number | null | undefined): string {
  return valeur === null || valeur === undefined ? '—' : formateurNombre.format(valeur);
}

/** Pourcentage, sans decimale inutile : `20 %`, `5,5 %`. */
export function pourcentage(valeur: number | null | undefined): string {
  return valeur === null || valeur === undefined ? '—' : `${formateurNombre.format(valeur)} %`;
}

/** Date seule (`2026-03-07`) au format `07/03/2026`. */
export function date(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const analysee = analyserDate(valeur);
  return analysee ? formateurDate.format(analysee) : '—';
}

/** Date en toutes lettres : `7 mars 2026`. */
export function dateLongue(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const analysee = analyserDate(valeur);
  return analysee ? formateurDateLongue.format(analysee) : '—';
}

/** Horodatage complet : `07/03/2026 09:26`. */
export function dateHeure(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const analysee = analyserDate(valeur);
  return analysee ? formateurDateHeure.format(analysee) : '—';
}

/**
 * Analyse une date de l'API.
 *
 * Les `DateOnly` du back arrivent en `AAAA-MM-JJ`. Les passer a `new Date()` les
 * interprete en UTC, ce qui affiche la veille pour tout fuseau a l'ouest de
 * Greenwich. On construit donc explicitement une date locale a midi : le decalage
 * horaire ne peut plus faire basculer le jour.
 */
function analyserDate(valeur: string): Date | null {
  const jourSeul = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valeur);

  if (jourSeul) {
    const [, annee, mois, jour] = jourSeul;
    return new Date(Number(annee), Number(mois) - 1, Number(jour), 12);
  }

  const analysee = new Date(valeur);
  return Number.isNaN(analysee.getTime()) ? null : analysee;
}

/** Date du jour au format attendu par l'API (`AAAA-MM-JJ`), en heure locale. */
export function aujourdhuiIso(): string {
  const maintenant = new Date();
  const mois = String(maintenant.getMonth() + 1).padStart(2, '0');
  const jour = String(maintenant.getDate()).padStart(2, '0');
  return `${maintenant.getFullYear()}-${mois}-${jour}`;
}

/** Ajoute des jours a une date ISO et renvoie le resultat au meme format. */
export function ajouterJours(iso: string, jours: number): string {
  const base = analyserDate(iso) ?? new Date();
  base.setDate(base.getDate() + jours);
  const mois = String(base.getMonth() + 1).padStart(2, '0');
  const jour = String(base.getDate()).padStart(2, '0');
  return `${base.getFullYear()}-${mois}-${jour}`;
}

/** Initiales pour un avatar : `Jean Dupont` -> `JD`. */
export function initiales(nom: string | null | undefined): string {
  if (!nom?.trim()) return '?';

  return nom
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toUpperCase())
    .join('');
}
